import React from 'react';
import { 
  X, 
  Package, 
  User, 
  DollarSign, 
  Calendar, 
  Clock, 
  MapPin, 
  Star, 
  StarOff,
  CheckCircle, 
  XCircle,
  Phone,
  Mail,
  Globe,
  Tag
} from 'lucide-react';
import Button from '../shared/Button';

const ServiceDetailsModal = ({ isOpen, onClose, service }) => {
  if (!isOpen || !service) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-4xl">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-12 w-12">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {service.service_name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Service Details
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Service Info */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Service Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <Package className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Service Name</p>
                        <p className="text-sm text-gray-600">{service.service_name}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Tag className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Category</p>
                        <p className="text-sm text-gray-600">
                          {service.service_category?.category_name || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <DollarSign className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Base Price</p>
                        <p className="text-sm text-gray-600">
                          ${parseFloat(service.base_price).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Clock className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Duration</p>
                        <p className="text-sm text-gray-600">
                          {service.duration_hours ? `${service.duration_hours} hours` : 'Not specified'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="h-5 w-5 mt-0.5 mr-3 flex items-center justify-center">
                        {service.is_active ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Status</p>
                        <p className={`text-sm font-medium ${
                          service.is_active ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {service.is_active ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="h-5 w-5 mt-0.5 mr-3 flex items-center justify-center">
                        {service.is_featured ? (
                          <Star className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <StarOff className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Featured</p>
                        <p className={`text-sm font-medium ${
                          service.is_featured ? 'text-yellow-600' : 'text-gray-600'
                        }`}>
                          {service.is_featured ? 'Featured' : 'Regular'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {service.description && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Description</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Right Column - Vendor Info */}
              <div className="space-y-6">
                {/* Vendor Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Vendor Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <User className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Vendor Name</p>
                        <p className="text-sm text-gray-600">
                          {/* {service.vendor_profile?.user?.name} {service.vendor_profile?.user?.last_name} */}
                        {/* </p> */}
                          {service.vendor_profile?.user?.name || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Mail className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Email</p>
                        <p className="text-sm text-gray-600">
                          {service.vendor_profile?.user?.email}
                        </p>
                      </div>
                    </div>
                    
                    {service.vendor_profile?.business_phone && (
                      <div className="flex items-start">
                        <Phone className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Business Phone</p>
                          <p className="text-sm text-gray-600">
                            {service.vendor_profile.business_phone}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {service.vendor_profile?.business_name && (
                      <div className="flex items-start">
                        <Globe className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Business Name</p>
                          <p className="text-sm text-gray-600">
                            {service.vendor_profile.business_name}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {service.vendor_profile?.business_address && (
                      <div className="flex items-start">
                        <MapPin className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Business Address</p>
                          <p className="text-sm text-gray-600">
                            {service.vendor_profile.business_address}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Packages */}
                {service.vendor_service_packages && service.vendor_service_packages.length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Service Packages</h4>
                    <div className="space-y-2">
                      {service.vendor_service_packages.map((pkg, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{pkg.package_name}</p>
                              <p className="text-xs text-gray-500">
                                ${parseFloat(pkg.base_price).toFixed(2)} • {pkg.duration_hours}h
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {pkg.is_popular && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Popular
                                </span>
                              )}
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                pkg.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {pkg.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Created Date */}
                <div className="flex items-start">
                  <Calendar className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Created</p>
                    <p className="text-sm text-gray-600">
                      {new Date(service.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-end">
              <Button onClick={onClose} variant="secondary">
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailsModal;
